import { useState, useCallback, useEffect, useRef } from "react";
import { useAuth } from "@/modules/auth";
import { useBrands } from "@/modules/brand";
import { usePlans } from "@/modules/plan";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import * as Dialog from "@radix-ui/react-dialog";
import ImageUploadField from "@/components/ui/image-upload-field";
import Container from "@/components/ui/container";
import Input from "@/components/ui/input";
import Button from "@/components/ui/button";

function dataURLtoFile(dataUrl: string, baseFilename: string): File {
	const arr = dataUrl.split(",");
	const mime = arr[0].match(/:(.*?);/)?.[1] || "image/jpeg";
	const bstr = atob(arr[1]);
	let n = bstr.length;
	const u8arr = new Uint8Array(n);
	while (n--) {
		u8arr[n] = bstr.charCodeAt(n);
	}
	const extMap: Record<string, string> = {
		"image/jpeg": ".jpg",
		"image/png": ".png",
		"image/gif": ".gif",
		"image/webp": ".webp",
	};
	const ext = extMap[mime] || ".jpg";
	const filename = baseFilename.replace(/\.[^/.]+$/, "") + ext;
	return new File([u8arr], filename, { type: mime });
}

const brandInfoSchema = z.object({
	name: z.string().min(1, "Brand name is required"),
	slug: z
		.string()
		.min(3, "Slug must be at least 3 characters")
		.regex(/^[a-z0-9-]+$/, "Slug can only contain lowercase letters, numbers, and hyphens")
		.regex(/^[^-]/, "Slug cannot start with a hyphen")
		.regex(/[^-]$/, "Slug cannot end with a hyphen")
		.regex(/^(?!.*-{2})/, "Slug cannot contain consecutive hyphens"),
	website_url: z.string().min(1, "Website URL is required").url("Please enter a valid URL"),
});

type BrandInfoFormData = z.infer<typeof brandInfoSchema>;
type SaveStatus = "idle" | "saving" | "saved" | "error";

export default function SettingsPage() {
	const { currentWorkspaceId } = useAuth();
	const { brands, selectedBrand, viewBrand, uploadBrandLogo, deleteBrandLogo, updateBrand, checkBrandSlugAvailability } = useBrands();
	const { createCheckout, isCheckoutLoading } = usePlans();
	const [isRedirecting, setIsRedirecting] = useState(false);

	const [logoUrl, setLogoUrl] = useState<string | null>(null);
	const [pendingLogoDataUrl, setPendingLogoDataUrl] = useState<string | null>(null); // New logo to upload
	const [logoMarkedForDeletion, setLogoMarkedForDeletion] = useState(false); // Track if logo should be deleted
	const originalLogoUrlRef = useRef<string | null>(null);
	const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(true);
	void isSlugManuallyEdited; // Available for future use
	const [isCheckingSlug, setIsCheckingSlug] = useState(false);
	const [slugError, setSlugError] = useState<string | null>(null);
	const [isSlugAvailable, setIsSlugAvailable] = useState(false);
	const [originalSlug, setOriginalSlug] = useState<string | null>(null);
	const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
	const [isSlugUnlocked, setIsSlugUnlocked] = useState(false);
	const [showSlugWarningModal, setShowSlugWarningModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [brandingEnabled, setBrandingEnabled] = useState(true);
	const [originalBrandingEnabled, setOriginalBrandingEnabled] = useState(true);

	// Track sidebar collapsed state for sticky footer positioning
	const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
		const saved = localStorage.getItem("sidebar-collapsed");
		return saved ? JSON.parse(saved) : false;
	});

	const currentBrand = brands?.find((b) => b.id === currentWorkspaceId);

	// Fetch full brand details for checkout session info
	useEffect(() => {
		if (currentWorkspaceId) {
			viewBrand(currentWorkspaceId);
		}
	}, [currentWorkspaceId, viewBrand]);

	// Check if there's a pending checkout session
	const completedStatuses = ["succeeded", "failed", "cancelled"];
	const hasPendingCheckout = selectedBrand?.checkout_session_status && !completedStatuses.includes(selectedBrand.checkout_session_status) && selectedBrand?.checkout_session_url;

	// Listen for sidebar collapse changes
	useEffect(() => {
		const handleSidebarChange = (e: CustomEvent<{ isCollapsed: boolean }>) => {
			setIsSidebarCollapsed(e.detail.isCollapsed);
		};

		window.addEventListener("sidebar-collapse-change", handleSidebarChange as EventListener);

		return () => {
			window.removeEventListener("sidebar-collapse-change", handleSidebarChange as EventListener);
		};
	}, []);

	const form = useForm<BrandInfoFormData>({
		resolver: zodResolver(brandInfoSchema),
		mode: "onChange",
		defaultValues: {
			name: "",
			slug: "",
			website_url: "",
		},
	});

	const {
		watch,
		formState: { errors, isDirty },
	} = form;
	const slug = watch("slug") || "";

	// Initialize form with current brand data
	useEffect(() => {
		if (currentBrand) {
			form.reset({
				name: currentBrand.name || "",
				slug: currentBrand.slug || "",
				website_url: currentBrand.website_url || "",
			});
			setOriginalSlug(currentBrand.slug || "");
			setLogoUrl(currentBrand.logo_url);
			originalLogoUrlRef.current = currentBrand.logo_url;
			setPendingLogoDataUrl(null);
			setLogoMarkedForDeletion(false);
			setIsSlugManuallyEdited(true);
			const brandingValue = currentBrand.branding !== "disabled";
			setBrandingEnabled(brandingValue);
			setOriginalBrandingEnabled(brandingValue);
		}
	}, [currentBrand, form]);

	// Clear success message when form becomes dirty
	useEffect(() => {
		if (isDirty) {
			setSaveStatus("idle");
		}
	}, [isDirty]);

	// Check slug availability
	useEffect(() => {
		const isValidSlug = slug && slug.length >= 3 && /^[a-z0-9-]+$/.test(slug) && /^[^-]/.test(slug) && /[^-]$/.test(slug) && !/.*-{2}/.test(slug);

		if (!slug || !isValidSlug) {
			setSlugError(null);
			setIsSlugAvailable(false);
			return;
		}

		// Skip availability check if slug matches the original
		if (originalSlug && slug === originalSlug) {
			setSlugError(null);
			setIsSlugAvailable(false);
			return;
		}

		setIsSlugAvailable(false);
		setSlugError(null);
		const timeoutId = setTimeout(async () => {
			setIsCheckingSlug(true);
			try {
				const isAvailable = await checkBrandSlugAvailability(slug);
				if (isAvailable) {
					setIsSlugAvailable(true);
				} else {
					setSlugError("This slug is already taken");
				}
			} catch {
				setSlugError("Failed to check slug availability");
			} finally {
				setIsCheckingSlug(false);
			}
		}, 500);

		return () => clearTimeout(timeoutId);
	}, [slug, originalSlug, checkBrandSlugAvailability]);

	// Logo change handler - only updates local state, doesn't upload
	const handleLogoChange = useCallback((dataUrl: string | null) => {
		if (dataUrl) {
			// New logo selected - show it locally and mark for upload
			setLogoUrl(dataUrl);
			setPendingLogoDataUrl(dataUrl);
			setLogoMarkedForDeletion(false);
		} else {
			// Logo cleared - mark for deletion
			setLogoUrl(null);
			setPendingLogoDataUrl(null);
			setLogoMarkedForDeletion(originalLogoUrlRef.current !== null);
		}
	}, []);

	// Check if there are logo changes
	const hasLogoChanges = pendingLogoDataUrl !== null || logoMarkedForDeletion;
	const hasBrandingChanges = brandingEnabled !== originalBrandingEnabled;
	const hasAnyChanges = isDirty || hasLogoChanges || hasBrandingChanges;

	const onSubmit = async (data: BrandInfoFormData) => {
		if (!currentWorkspaceId) return;

		setSaveStatus("saving");

		try {
			// Save form fields if changed
			if (isDirty) {
				const result = await updateBrand(currentWorkspaceId, {
					name: data.name.trim(),
					slug: data.slug.trim(),
					website_url: data.website_url.trim(),
				});

				if (result) {
					const newSlug = data.slug;
					setOriginalSlug(newSlug);
					form.reset(
						{
							name: data.name,
							slug: data.slug,
							website_url: data.website_url,
						},
						{ keepDefaultValues: false },
					);
				} else {
					setSaveStatus("error");
					setTimeout(() => setSaveStatus("idle"), 3000);
					return;
				}
			}

			// Handle logo changes
			if (logoMarkedForDeletion) {
				await deleteBrandLogo(currentWorkspaceId);
				originalLogoUrlRef.current = null;
				setLogoMarkedForDeletion(false);
			} else if (pendingLogoDataUrl) {
				const file = dataURLtoFile(pendingLogoDataUrl, "brand-logo.jpg");
				const result = await uploadBrandLogo(currentWorkspaceId, file);
				if (result) {
					setLogoUrl(result.logo_url);
					originalLogoUrlRef.current = result.logo_url;
				}
				setPendingLogoDataUrl(null);
			}

			// Handle branding changes
			if (hasBrandingChanges) {
				const result = await updateBrand(currentWorkspaceId, {
					branding: brandingEnabled ? "enabled" : "disabled",
				});
				if (result) {
					setOriginalBrandingEnabled(brandingEnabled);
				} else {
					setSaveStatus("error");
					setTimeout(() => setSaveStatus("idle"), 3000);
					return;
				}
			}

			setSaveStatus("saved");
			setTimeout(() => setSaveStatus("idle"), 2000);
		} catch (error) {
			console.error("Failed to update brand:", error);
			setSaveStatus("error");
			setTimeout(() => setSaveStatus("idle"), 3000);
		}
	};

	const handleDiscard = () => {
		if (currentBrand) {
			form.reset(
				{
					name: currentBrand.name || "",
					slug: currentBrand.slug || "",
					website_url: currentBrand.website_url || "",
				},
				{ keepDefaultValues: false },
			);
			// Reset logo state
			setLogoUrl(originalLogoUrlRef.current);
			setPendingLogoDataUrl(null);
			setLogoMarkedForDeletion(false);
			// Reset branding state
			setBrandingEnabled(originalBrandingEnabled);
			setSaveStatus("idle");
		}
	};

	const handleBrandingToggle = () => {
		setBrandingEnabled(!brandingEnabled);
	};

	const handleUpgrade = async () => {
		if (!currentWorkspaceId) return;

		// If there's a pending checkout, use the existing URL
		if (hasPendingCheckout && selectedBrand?.checkout_session_url) {
			setIsRedirecting(true);
			window.location.href = selectedBrand.checkout_session_url;
			return;
		}

		// Otherwise, create a new checkout session
		const result = await createCheckout(currentWorkspaceId);

		if (result?.checkout_session_url) {
			window.location.href = result.checkout_session_url;
		}
	};

	return (
		<div className="relative py-12">
			<Container className="pb-20">
				{/* Page Header */}
				<div className="mb-8">
					<h1 className="text-xl font-semibold text-primary">Settings</h1>
					<p className="mt-1 text-sm text-tertiary">Manage your brand settings and preferences</p>
				</div>

				<div className="space-y-10">
					{/* Brand Information Section */}
					<form onSubmit={form.handleSubmit(onSubmit)}>
						<section className="bg-white rounded-xl border border-gray-200 p-6">
							<div className="mb-6">
								<h2 className="text-sm font-semibold text-primary uppercase tracking-wide">Brand Information</h2>
								<p className="text-sm text-tertiary mt-1">Update your brand details. These will be visible on your public page.</p>
							</div>

							<div className="space-y-6">
								{/* Brand Name */}
								<Input
									id="brand-name"
									label="Brand Name"
									type="text"
									placeholder="Enter your brand name"
									required
									disabled={saveStatus === "saving"}
									error={errors.name?.message}
									{...form.register("name")}
								/>

								{/* Public URL */}
								<div>
									<div className="flex items-end gap-3">
										<div className="flex-1">
											<Input
												id="brand-slug"
												label="Public URL"
												type="text"
												placeholder="your-brand"
												required
												disabled={!isSlugUnlocked || saveStatus === "saving"}
												error={isSlugUnlocked ? errors.slug?.message : undefined}
												inputPrefix={<span className="text-tertiary text-sm">whatpeople.sayabout.us/</span>}
												{...form.register("slug", {
													onChange: () => setIsSlugManuallyEdited(true),
												})}
											/>
										</div>
										{!isSlugUnlocked && (
											<button
												type="button"
												onClick={() => setShowSlugWarningModal(true)}
												className="shrink-0 h-[38px] px-4 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
											>
												Change
											</button>
										)}
									</div>
									{isSlugUnlocked && slug && !errors.slug?.message ? (
										<p className={`mt-1.5 text-sm ${isCheckingSlug ? "text-gray-500" : slugError ? "text-red-600" : isSlugAvailable ? "text-green-600" : "text-tertiary"}`}>
											{isCheckingSlug ? "Checking availability..." : slugError ? slugError : isSlugAvailable ? "This URL is available!" : "This is your unique public page URL"}
										</p>
									) : !isSlugUnlocked ? (
										<p className="mt-1.5 text-sm text-tertiary">This is your unique public page URL</p>
									) : null}
								</div>

								{/* Website URL */}
								<Input
									id="brand-website"
									label="Website or Social Media URL"
									type="url"
									placeholder="https://example.com"
									required
									disabled={saveStatus === "saving"}
									error={errors.website_url?.message}
									message={{ text: "Your website or social media profile link", status: "message" }}
									{...form.register("website_url")}
								/>
							</div>
						</section>
					</form>

					{/* Brand Logo Section */}
					<section className="bg-white rounded-xl border border-gray-200 p-6">
						<div className="mb-6">
							<h2 className="text-sm font-semibold text-primary uppercase tracking-wide">Brand Logo</h2>
							<p className="text-sm text-tertiary mt-1">Upload your brand logo. This will appear on your public page and forms.</p>
						</div>

						<div className="flex items-start gap-6">
							<div className="flex-shrink-0">
								<ImageUploadField value={logoUrl} onChange={handleLogoChange} aspectRatio={1} maxSizeMB={5} className="w-24 h-24" disabled={saveStatus === "saving"} />
							</div>
							<div className="flex-1 pt-1">
								<p className="text-sm text-gray-500">Recommended size: 512x512 pixels.</p>
								<p className="mt-1 text-xs text-gray-400">Supports JPG, PNG, GIF, WebP. Max 5MB.</p>
							</div>
						</div>
					</section>

					{/* Powered By Section */}
					<section className="bg-white rounded-xl border border-gray-200 p-6">
						<div className="mb-6">
							<h2 className="text-sm font-semibold text-primary uppercase tracking-wide">Powered By Badge</h2>
							<p className="text-sm text-tertiary mt-1">Control the visibility of the "build social proof with Say About Us" badge across your brand.</p>
						</div>

						{(() => {
							const canRemoveBranding = currentBrand?.can_disable_branding ?? false;

							if (canRemoveBranding) {
								// Premium plan - show toggle
								return (
									<div className="flex items-center justify-between gap-4">
										<label htmlFor="powered-by-toggle" className="flex-1 cursor-pointer">
											<span className="block text-sm font-medium text-primary">Show Powered By</span>
											<span className="block text-sm text-tertiary mt-0.5">
												Display the "build social proof with Say About Us" badge on all your forms, widgets, and Walls of Love.
											</span>
										</label>
										<button
											type="button"
											role="switch"
											aria-checked={brandingEnabled}
											disabled={saveStatus === "saving"}
											onClick={handleBrandingToggle}
											className={`relative shrink-0 w-10 h-6 rounded-full transition-colors focus:ring-2 focus:ring-gray-800 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed ${
												brandingEnabled ? "bg-gray-800" : "bg-gray-200"
											}`}
										>
											<span
												className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${
													brandingEnabled ? "translate-x-4" : "translate-x-0"
												}`}
											/>
										</button>
									</div>
								);
							}

							// Free plan - show upgrade banner
							return (
								<div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-5 py-4">
									<div className="shrink-0 w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
										<svg className="w-5 h-5 text-gray-600" viewBox="0 0 24 24" fill="currentColor">
											<path d="M13 10V3L4 14h7v7l9-11h-7z" />
										</svg>
									</div>
									<div className="flex-1">
										<p className="text-sm font-medium text-gray-900">Upgrade to remove branding</p>
										<p className="text-sm text-gray-500 mt-0.5">Remove the "Powered by Say About Us" badge from all your forms, widgets, and Walls of Love.</p>
									</div>
									<div className="shrink-0 flex items-center gap-4">
										<div className="flex items-center gap-1.5">
											<span className="text-xs text-gray-400">Powered by</span>
											<img src="/images/logo-dodopayments-full.svg" alt="Dodo Payments" className="h-8" />
										</div>
										<button
											type="button"
											onClick={handleUpgrade}
											disabled={isCheckoutLoading || isRedirecting}
											className="px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
										>
											{isCheckoutLoading || isRedirecting ? (
												<span className="flex items-center gap-2">
													<svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
														<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
														<path
															className="opacity-75"
															fill="currentColor"
															d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
														/>
													</svg>
													Redirecting...
												</span>
											) : (
												"Upgrade"
											)}
										</button>
									</div>
								</div>
							);
						})()}
					</section>

					{/* Danger Zone Section */}
					<section className="bg-white rounded-xl border border-red-200 p-6">
						<div className="mb-6">
							<h2 className="text-sm font-semibold text-red-600 uppercase tracking-wide">Danger Zone</h2>
							<p className="text-sm text-tertiary mt-1">Irreversible and destructive actions for your brand workspace.</p>
						</div>

						<div className="flex items-center justify-between gap-4 bg-red-50 border border-red-100 rounded-xl px-5 py-4">
							<div className="flex-1">
								<p className="text-sm font-medium text-gray-900">Delete this brand workspace</p>
								<p className="text-sm text-gray-500 mt-0.5">
									Once deleted, all data including reviews, forms, widgets, and Walls of Love will be permanently removed. This action cannot be undone.
								</p>
							</div>
							<button
								type="button"
								onClick={() => setShowDeleteModal(true)}
								className="shrink-0 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
							>
								Delete Brand
							</button>
						</div>
					</section>
				</div>
			</Container>

			{/* Sticky footer with Save/Discard buttons */}
			<div
				className={`fixed bottom-0 left-0 z-20 bg-white border-t border-gray-200 px-8 py-4 transition-all duration-300 ${isSidebarCollapsed ? "w-[calc(100%-64px)] ml-16" : "w-[calc(100%-256px)] ml-64"}`}
			>
				<Container>
					<div className="flex items-center justify-end">
						<div className="flex items-center gap-3">
							{/* Status indicator */}
							{saveStatus !== "idle" && (
								<span
									className={`text-sm font-medium flex items-center gap-1.5 transition-opacity duration-200 ${
										saveStatus === "saving" ? "text-gray-500" : saveStatus === "saved" ? "text-green-600" : "text-red-600"
									}`}
								>
									{saveStatus === "saving" ? (
										<>
											<svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
												<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
												<path
													className="opacity-75"
													fill="currentColor"
													d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
												/>
											</svg>
											Saving...
										</>
									) : saveStatus === "saved" ? (
										<>
											<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
												<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
											</svg>
											Saved
										</>
									) : (
										<>
											<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
												<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
											</svg>
											Error saving
										</>
									)}
								</span>
							)}

							{/* Discard button */}
							<Button type="button" variant="light" onClick={handleDiscard} disabled={!hasAnyChanges || saveStatus === "saving" || saveStatus === "saved"}>
								Discard
							</Button>

							{/* Save button */}
							<Button
								type="button"
								variant="default"
								onClick={form.handleSubmit(onSubmit)}
								disabled={!hasAnyChanges || saveStatus === "saving" || saveStatus === "saved" || isCheckingSlug || !!slugError}
							>
								{saveStatus === "saving" ? "Saving..." : "Save changes"}
							</Button>
						</div>
					</div>
				</Container>
			</div>

			{/* Slug Change Warning Modal */}
			<Dialog.Root open={showSlugWarningModal} onOpenChange={setShowSlugWarningModal}>
				<Dialog.Portal>
					<Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
					<Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-full max-w-md overflow-hidden">
						<div className="p-6">
							{/* Warning Icon */}
							<div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
								<svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
									/>
								</svg>
							</div>

							<Dialog.Title className="text-lg font-semibold text-gray-900">Change Public URL?</Dialog.Title>
							<Dialog.Description className="text-sm text-gray-500 mt-2">Changing your public URL will have the following effects:</Dialog.Description>

							<ul className="mt-4 space-y-2">
								<li className="flex items-start gap-2 text-sm text-gray-600">
									<svg className="w-4 h-4 text-red-500 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
										<path
											fillRule="evenodd"
											d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
											clipRule="evenodd"
										/>
									</svg>
									<span>All existing links to your public page will break</span>
								</li>
								<li className="flex items-start gap-2 text-sm text-gray-600">
									<svg className="w-4 h-4 text-red-500 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
										<path
											fillRule="evenodd"
											d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
											clipRule="evenodd"
										/>
									</svg>
									<span>Embedded widgets and forms may stop working</span>
								</li>
								<li className="flex items-start gap-2 text-sm text-gray-600">
									<svg className="w-4 h-4 text-red-500 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
										<path
											fillRule="evenodd"
											d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
											clipRule="evenodd"
										/>
									</svg>
									<span>Bookmarks and saved links will no longer work</span>
								</li>
							</ul>

							<div className="mt-6 flex gap-3">
								<Button type="button" variant="light" className="flex-1" onClick={() => setShowSlugWarningModal(false)}>
									Cancel
								</Button>
								<Button
									type="button"
									variant="default"
									className="flex-1"
									onClick={() => {
										setIsSlugUnlocked(true);
										setShowSlugWarningModal(false);
									}}
								>
									I understand, continue
								</Button>
							</div>
						</div>

						<Dialog.Close asChild>
							<button className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
								<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</Dialog.Close>
					</Dialog.Content>
				</Dialog.Portal>
			</Dialog.Root>

			{/* Delete Brand Modal */}
			<Dialog.Root open={showDeleteModal} onOpenChange={setShowDeleteModal}>
				<Dialog.Portal>
					<Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
					<Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-full max-w-md overflow-hidden">
						<div className="p-6">
							{/* Warning Icon */}
							<div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
								<svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
									/>
								</svg>
							</div>

							<Dialog.Title className="text-lg font-semibold text-gray-900">Delete Brand Workspace</Dialog.Title>
							<Dialog.Description className="text-sm text-gray-500 mt-2">
								To delete your brand workspace and all associated data, please send us an email requesting account deletion.
							</Dialog.Description>

							<div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
								<p className="text-sm text-gray-600 mb-2">Send your request to:</p>
								<a href="mailto:canyou@sayabout.us?subject=Delete Brand Workspace Request" className="text-sm font-medium text-gray-900 hover:text-gray-700 underline">
									canyou@sayabout.us
								</a>
								<p className="text-xs text-gray-500 mt-3">Please include your brand name and registered email address in the request. We will respond as soon as we can.</p>
							</div>

							<div className="mt-6">
								<Button type="button" variant="light" className="w-full" onClick={() => setShowDeleteModal(false)}>
									Close
								</Button>
							</div>
						</div>

						<Dialog.Close asChild>
							<button className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
								<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</Dialog.Close>
					</Dialog.Content>
				</Dialog.Portal>
			</Dialog.Root>
		</div>
	);
}
